import React from "react";
import { Link } from "react-router-dom";
import Alert from "web-ui/components/internal/Alert";
import Button from "web-ui/components/internal/Button";
import Drawer from "web-ui/components/internal/Drawer";
import Dropdown from "web-ui/components/internal/Dropdown";
import Icon, { iconFactory } from "web-ui/components/internal/Icon";
import Menu from "web-ui/components/internal/Menu";
import MenuItem from "web-ui/components/internal/MenuItem";
import Select from "web-ui/components/internal/Select";
import Table from "web-ui/components/internal/Table";
import TextInput from "web-ui/components/internal/TextInput";
import useDismissable from "web-ui/components/useDismissable";

import { type VoiceListVoice, toVoiceListVoice } from "../../api/generalTts";
import type {
  VoiceEvalResults,
  VoicesV2Item,
} from "../../api/ttsGenerationBackend";
import {
  getCustomVoiceEndpointStatuses,
  getTtsGenerationAudio,
  getVoiceDetail,
  spinUpCustomVoiceEndpoint,
} from "../../api/ttsGenerationBackend";

import AccordionPanel from "./AccordionPanel";
import EditVoiceModal from "./EditVoiceModal";
import styles from "./VoiceDrawer.module.scss";
import {
  formatCharacter,
  formatLanguage,
  formatProvider,
  titleCaseFormatter,
} from "./VoiceFinder";

interface Props {
  className?: string;
  exampleSentence?: string;
  open?: boolean;
  onChange?: (type: "clone" | "edit", voice: VoicesV2Item) => void;
  onRequestClose?: () => void;
  voice?: VoiceListVoice;
}

const VoiceDrawer = ({
  className,
  exampleSentence,
  onChange,
  onRequestClose,
  open,
  voice,
}: Props) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const [showModal, setShowModal] = React.useState<"clone" | "edit" | false>(
    false,
  );
  const [selectedVersion, setSelectedVersion] = React.useState(voice?.version);

  React.useEffect(() => {
    setSelectedVersion(voice?.version);
  }, [voice?.name, voice?.provider, voice?.version]);

  const selectedVoice = React.useMemo(() => {
    if (!voice || voice.allVersions.length === 0) {
      return voice;
    }
    const found = voice.allVersions.find(v => v.version === selectedVersion);
    return found ? toVoiceListVoice(found, voice.allVersions) : voice;
  }, [voice, selectedVersion]);

  const { ref: menuRef } = useDismissable({
    onRequestClose: () => {
      // If clicking on the reference button, allow that click to be processed
      // before closing the dropdown.
      setTimeout(() => setShowMenu(false), 0);
    },
    open: showMenu,
  });

  return (
    <>
      <Drawer
        className={className}
        headerRight={
          voice === undefined || voice.provider === "recorded" ? null : (
            <div className={styles["menu-container"]}>
              <Button<"button">
                isGhost={true}
                isIconOnly={true}
                onClick={e => {
                  // If the dropdown is shown, clicks outside it are handled by its
                  // closing mechanism.
                  if (
                    showMenu &&
                    (e.nativeEvent as PointerEvent).pointerId !== -1
                  ) {
                    return;
                  }
                  setShowMenu(!showMenu);
                }}
                size="s"
              >
                <Icon name="ellipsis-vertical" size="s" />
              </Button>
              {showMenu ? (
                <Dropdown
                  className={styles["menu-dropdown"]}
                  ref={menuRef}
                  type="menu"
                >
                  <Menu className={styles.menu}>
                    <MenuItem
                      leadingVisual={iconFactory("edit")}
                      onClick={() => {
                        setShowModal("edit");
                        setShowMenu(false);
                      }}
                    >
                      Edit voice
                    </MenuItem>
                    <MenuItem
                      leadingVisual={iconFactory("foreground")}
                      onClick={() => {
                        setShowModal("clone");
                        setShowMenu(false);
                      }}
                    >
                      Clone voice
                    </MenuItem>
                  </Menu>
                </Dropdown>
              ) : null}
            </div>
          )
        }
        headerText={voice?.name}
        modal={false}
        onRequestClose={() => {
          if (!showModal) {
            onRequestClose?.();
          }
        }}
        open={open}
      >
        <VoiceDrawerContents
          exampleSentence={exampleSentence}
          key={voice?.name}
          onVersionChange={setSelectedVersion}
          selectedVersion={selectedVersion}
          voice={voice}
        />
      </Drawer>
      {showModal && selectedVoice ? (
        <EditVoiceModal
          mode={showModal}
          onChange={(mode, clone) => {
            onChange?.(mode, clone);
            setShowModal(false);
          }}
          onRequestClose={() => setShowModal(false)}
          voice={selectedVoice}
        />
      ) : null}
    </>
  );
};

interface DrawerContentsProps {
  exampleSentence?: string;
  onVersionChange?: (version: number) => void;
  selectedVersion?: number;
  voice?: VoiceListVoice;
}

// eslint-disable-next-line complexity
const VoiceDrawerContents = ({
  exampleSentence = "",
  onVersionChange,
  selectedVersion,
  voice,
}: DrawerContentsProps) => {
  const [azureError, setAzureError] = React.useState<string>();
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [previewText, setPreviewText] = React.useState(exampleSentence);

  const displayedVersion = React.useMemo(() => {
    if (!voice) {
      return undefined;
    }
    if (voice.allVersions.length === 0) {
      return voice as unknown as VoicesV2Item;
    }
    return (
      voice.allVersions.find(v => v.version === selectedVersion) ??
      voice.allVersions[0]
    );
  }, [voice, selectedVersion]);

  const isUnavailable = displayedVersion?.status === "unavailable";

  const createEvaluationParams = React.useMemo(() => {
    if (!displayedVersion) {
      return undefined;
    }
    const params = new URLSearchParams();
    params.set("languageId", displayedVersion.primary_language.split("-")[0]);
    params.set("provider", displayedVersion.provider);
    params.set("voiceId", displayedVersion.id);
    params.set("voiceName", displayedVersion.name);
    return params;
  }, [displayedVersion]);

  const [evalResults, setEvalResults] = React.useState<VoiceEvalResults | null>(
    null,
  );

  React.useEffect(() => {
    if (!voice || !selectedVersion || voice.provider === "recorded") {
      setEvalResults(null);
      return undefined;
    }
    let cancelled = false;
    getVoiceDetail(voice.name, selectedVersion).then(
      data => {
        if (!cancelled) {
          setEvalResults(data.quality ?? null);
        }
      },
      () => {
        if (!cancelled) {
          setEvalResults(null);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [voice, selectedVersion]);

  const handlePlay = async () => {
    if (!displayedVersion) {
      return;
    }
    setIsPlaying(true);
    try {
      if (
        displayedVersion.world_character &&
        displayedVersion.provider === "azure"
      ) {
        const status = await (async () => {
          const statuses = await getCustomVoiceEndpointStatuses([
            displayedVersion.name,
            displayedVersion.provider_parameters.model_name,
          ]);
          return (
            statuses[displayedVersion.name] ??
            statuses[displayedVersion.provider_parameters.model_name]
          );
        })();
        switch (status) {
          case "ACTIVE":
            setAzureError(undefined);
            break;
          case "SPINNING_DOWN":
          case "SUSPENDED":
            spinUpCustomVoiceEndpoint(displayedVersion.name);
            setAzureError("Spinning up…, try again in a bit");
            return;
          case "SPINNING_UP":
            setAzureError("Spinning up…, try again in a bit");
            return;
          case "UNKNOWN":
            setAzureError("Unknown error");
            return;
          default:
            return;
        }
      } else {
        setAzureError(undefined);
      }
      const mp3Data = await getTtsGenerationAudio({
        featureName: "voice_preview",
        languageId: displayedVersion.primary_language,
        text: previewText,
        voiceName: displayedVersion.name,
        voiceVersion: displayedVersion.version,
      });
      const url = URL.createObjectURL(mp3Data);
      const audio = new Audio();
      audio.src = url;
      audio.addEventListener("ended", () => URL.revokeObjectURL(url));
      await audio.play();
    } finally {
      setIsPlaying(false);
    }
  };

  return (
    <>
      {isUnavailable ? (
        <Alert
          className={styles.alert}
          color="amber"
          description="Voice is marked unavailable by provider"
          leadingVisual={iconFactory("warning")}
        />
      ) : null}
      <div className={styles.panels}>
        <AccordionPanel header="Preview audio">
          <div className={styles.preview}>
            <Button
              isIconOnly={true}
              onClick={handlePlay}
              size="s"
              state={
                isPlaying
                  ? "loading"
                  : !isUnavailable &&
                      displayedVersion?.primary_language &&
                      previewText
                    ? "enabled"
                    : "disabled"
              }
            >
              <Icon color="inherit" name="play" size="s" variant="inverse" />
            </Button>
            <TextInput
              className={styles.input}
              helperText={azureError}
              onChange={e => setPreviewText(e.value)}
              placeholder="Add text to preview the voice"
              size="s"
              state={azureError === undefined ? "enabled" : "error"}
              value={previewText}
            />
          </div>
        </AccordionPanel>
        {displayedVersion === undefined ? null : (
          <AccordionPanel header="About">
            <Table className={styles.table} outerBorder={false} size="s">
              <tbody>
                <tr>
                  <td>Voice name</td>
                  <td className={styles["table-value"]}>
                    {displayedVersion.name}
                  </td>
                </tr>
                <tr>
                  <td>Model ID</td>
                  <td className={styles["table-value"]}>
                    {displayedVersion.provider_parameters.model_name}
                  </td>
                </tr>
                <tr>
                  <td>Provider</td>
                  <td className={styles["table-value"]}>
                    {formatProvider(displayedVersion.provider)}
                  </td>
                </tr>
                <tr>
                  <td>Version</td>
                  <td className={styles["table-value"]}>
                    {voice && voice.allVersions.length > 1 ? (
                      <Select
                        displayText={`V${selectedVersion}${displayedVersion.is_default_version ? " (Default)" : ""}`}
                        size="s"
                      >
                        <Menu>
                          {voice.allVersions.map(v => (
                            <MenuItem
                              key={v.version}
                              onChange={e => {
                                if (e.selected) {
                                  onVersionChange?.(v.version);
                                }
                              }}
                              state={
                                v.version === selectedVersion
                                  ? "selected"
                                  : "enabled"
                              }
                            >
                              V{v.version}
                              {v.is_default_version ? " (Default)" : ""}
                            </MenuItem>
                          ))}
                        </Menu>
                      </Select>
                    ) : (
                      <>
                        V{displayedVersion.version}
                        {displayedVersion.is_default_version
                          ? " (Default)"
                          : ""}
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Language</td>
                  <td className={styles["table-value"]}>
                    {formatLanguage(displayedVersion.primary_language)}
                  </td>
                </tr>
                <tr>
                  <td>Multilingual</td>
                  <td className={styles["table-value"]}>
                    {displayedVersion.is_multilingual ? "Yes" : "No"}
                  </td>
                </tr>
                <tr>
                  <td>Gender</td>
                  <td className={styles["table-value"]}>
                    {displayedVersion.gender === null
                      ? "N/A"
                      : titleCaseFormatter(displayedVersion.gender)}
                  </td>
                </tr>
                <tr>
                  <td>Character</td>
                  <td className={styles["table-value"]}>
                    {displayedVersion.world_character === null
                      ? "N/A"
                      : formatCharacter(displayedVersion.world_character)}
                  </td>
                </tr>
                <tr>
                  <td>Age</td>
                  <td className={styles["table-value"]}>
                    {displayedVersion.age === null
                      ? "N/A"
                      : titleCaseFormatter(displayedVersion.age)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </AccordionPanel>
        )}
        {displayedVersion === undefined ? null : (
          <AccordionPanel header="Provider parameters">
            <pre className={styles["provider-params"]}>
              {JSON.stringify(displayedVersion.provider_parameters, null, 2)}
            </pre>
          </AccordionPanel>
        )}
        <AccordionPanel header="Quality">
          {evalResults &&
          Object.keys(evalResults.aggregateScores).length > 0 ? (
            <Table className={styles.table} outerBorder={false} size="s">
              <tbody>
                {Object.entries(evalResults.aggregateScores).map(([key, s]) => (
                  <tr key={key}>
                    <td>{s.dimension}</td>
                    <td
                      className={styles["table-value"]}
                      title={`n=${s.count}`}
                    >
                      {s.avg.toFixed(2)} / {s.scaleMax}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <span className={styles["text-empty"]}>
              No evaluation data available
            </span>
          )}
        </AccordionPanel>
        <AccordionPanel header="Evaluations">
          <Link
            className={styles.link}
            target="_blank"
            to={`${process.env.REACT_APP_TTS_EVALUATION_ORIGIN}/v2/admin/create-evaluation?${createEvaluationParams?.toString()}`}
          >
            Create new
          </Link>
          {evalResults && evalResults.evals.length > 0 ? (
            <Table className={styles.table} outerBorder={false} size="s">
              <tbody>
                {evalResults.evals.map(ev => {
                  const allNames = evalResults.evals
                    .map(e => e.evalName)
                    .join(",");
                  return (
                    <tr key={ev.evalName}>
                      <td>
                        <a
                          className={styles.evalLink}
                          href={`${process.env.REACT_APP_TTS_EVALUATION_ORIGIN}/v2/dashboard?config_names=${encodeURIComponent(allNames)}&open=${encodeURIComponent(ev.evalName)}`}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {ev.evalName}
                        </a>
                      </td>
                      <td className={styles["table-value"]}>
                        {new Date(ev.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : null}
        </AccordionPanel>
        {displayedVersion === undefined ? null : (
          <>
            <AccordionPanel header="Notes">
              <span
                className={
                  displayedVersion.notes ? styles.text : styles["text-empty"]
                }
              >
                {displayedVersion.notes || "No notes added"}
              </span>
            </AccordionPanel>
            <AccordionPanel header="History">
              <Table className={styles.table} outerBorder={false} size="s">
                <tbody>
                  {isUnavailable ? (
                    <tr>
                      <td>Marked unavailable</td>
                      <td className={styles["table-date"]}>N/A</td>
                    </tr>
                  ) : null}
                  {displayedVersion.last_updated_at !== null &&
                  displayedVersion.last_updated_at !==
                    displayedVersion.added_at ? (
                    <tr>
                      <td>Last updated</td>
                      <td className={styles["table-date"]}>
                        {new Date(
                          displayedVersion.last_updated_at,
                        ).toLocaleDateString()}{" "}
                        by {displayedVersion.last_updated_by}
                      </td>
                    </tr>
                  ) : null}
                  <tr>
                    <td>Voice added</td>
                    <td className={styles["table-date"]}>
                      {displayedVersion.added_at
                        ? new Date(
                            displayedVersion.added_at,
                          ).toLocaleDateString()
                        : "N/A"}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </AccordionPanel>
          </>
        )}
      </div>
    </>
  );
};

export default VoiceDrawer;
